// src/utils/BackButtonHandler.ts
import { Signal } from "../app/Signals";

export class BackButtonHandler {
	private static instance: BackButtonHandler;
	private handlers: Array<() => boolean> = []; // Возвращает true если нужно прервать навигацию
	private isAndroid = /Android/i.test(navigator.userAgent);
	private lastBackButtonPress = 0;

	// Сигнал для уведомлений
	public toastSignal: Signal<{ message: string, show: boolean }> = new Signal<{ message: string, show: boolean }>("/back-button/toast");

	private constructor() {
		if (this.isAndroid){
			this.init();
		}
	}

	static getInstance(): BackButtonHandler {
		if (!BackButtonHandler.instance) {
			BackButtonHandler.instance = new BackButtonHandler();
		}
		return BackButtonHandler.instance;
	}

	private init(): void {
		//console.log("init");
		window.addEventListener('popstate', this.handlePopState);

		// Используем history.pushState для создания истории.
		// Добавляем начальную запись в историю, чтобы предотвратить закрытие приложения.
		if (history.state === null) {
			history.replaceState({ backButtonHandled: true, initial: true }, '', window.location.href);
		} else {
			// Проверяем, есть ли уже метка backButtonHandled в текущем состоянии
			if (!history.state?.backButtonHandled) {
				// Если нет, добавляем её
				history.replaceState({ ...history.state, backButtonHandled: true, initial: false }, '', window.location.href);
			} else {
				// Если уже есть, просто добавляем новую запись для дополнительной защиты
				history.pushState({ backButtonHandled: true, initial: false }, '', window.location.href);
			}
		}


		// Добавляем обработчик для каждого изменения состояния истории,
		// чтобы гарантировать, что всегда есть хотя бы одна запись
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = (...args) => {
			originalPushState.apply(history, args);
			// Убедимся, что у нас всегда есть хотя бы одна запись с меткой
			if (!history.state?.backButtonHandled) {
				history.replaceState({ ...history.state, backButtonHandled: true, initial: false }, '', window.location.href);
			}
		};

		history.replaceState = (...args) => {
			originalReplaceState.apply(history, args);
			// Убедимся, что у нас всегда есть хотя бы одна запись с меткой
			if (!history.state?.backButtonHandled) {
				history.replaceState({ ...history.state, backButtonHandled: true, initial: false }, '', window.location.href);
			}
		};
	}

	private showToast(message: string): void {
		// Показываем уведомление через сигнал
		this.toastSignal.emit({ message, show: true });

		// Через 2 секунды скрываем уведомление
		setTimeout(() => {
			this.toastSignal.emit({ message: '', show: false });
		}, 2000);
	}

	private handlePopState = (event: PopStateEvent): boolean => {
		//		console.log("pop state");
		let isHandled = false;
		// Проверяем все обработчики
		for (const handler of this.handlers) {
			handler();
			isHandled = true;
		}

		setTimeout(()=>{
			// Добавляем новую запись в историю, чтобы избежать закрытия приложения
			history.pushState({ backButtonHandled: true, initial: false }, '', window.location.href+"#back"+Date.now());
		}, 10);

		const lookit = window.history;
		const state = lookit.state;
		if (isHandled) return false;

		// Проверяем, был ли последний раз нажата кнопка "назад" менее 2 секунд назад
		const currentTime = new Date().getTime();
		const timeDifference = currentTime - (this.lastBackButtonPress || 0);

		if (timeDifference < 2000) { // 2 секунды
			// Второе нажатие в течение 2 секунд - разрешаем закрытие приложения
			// В PWA на Android это приведет к закрытию приложения
			// Мы не можем программно закрыть PWA, но можем позволить стандартному поведению произойти
			// Для этого просто не добавляем новую запись и позволяем событию пройти дальше
			// Но для предотвращения закрытия приложения, всё равно добавим новую запись
			// и не выполняем никаких дополнительных действий
		} else {
			// Первое нажатие - показываем сообщение пользователю
			this.showToast('Нажмите еще раз для выхода');
			this.lastBackButtonPress = currentTime;
		}

		// // ВАЖНО: Всегда добавляем новую запись в историю, чтобы гарантировать,
		// // что мы никогда не достигнем начальной точки и приложение не закроется
		// setTimeout(()=> {
		// 	history.pushState({backButtonHandled: true, initial: false}, '', window.location.href);
		// }, 10);
		return false;
	};

	/**
     * Регистрирует обработчик кнопки "Назад"
     * @param handler Функция, возвращающая true если нужно прервать навигацию
     * @returns Функция для отмены регистрации
     */
	register(handler: () => boolean): () => void {
		this.handlers.push(handler);

		return () => {
			const index = this.handlers.indexOf(handler);
			if (index > -1) {
				this.handlers.splice(index, 1);
			}
		};
	}

	/**
     * Для ситуаций, когда нужно имитировать нажатие кнопки "Назад"
     */
	triggerBack(): void {
		window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
	}

	dispose(): void {
		//console.log("dispose");
		window.removeEventListener('popstate', this.handlePopState);
		this.handlers = [];
	}
}